import { useMemo, useState } from 'react';
import { formatDistance } from '../game/geo';
import { totalScore, type GameState } from '../game/gameState';
import { getGameMap } from '../game/maps';
import { formatScore, gameRank, MAX_GAME_SCORE, MAX_ROUND_SCORE } from '../game/scoring';
import { formatTimeLimit } from '../game/settings';
import { useAnimatedNumber } from '../hooks/useAnimatedNumber';
import { getProvider } from '../providers';
import { IconCopy, IconHome, IconRefresh, IconTrophy } from './Icons';
import MapView from './map/MapView';

interface SummaryProps {
  state: GameState;
  onReplay: () => void;
  onHome: () => void;
}

export default function Summary({ state, onReplay, onHome }: SummaryProps) {
  const total = totalScore(state.rounds);
  const animatedTotal = useAnimatedNumber(total, 1600);
  const map = getGameMap(state.settings.mapId);
  const [copied, setCopied] = useState<'ok' | 'error' | null>(null);
  const pairs = useMemo(
    () => state.rounds.map((r, i) => ({ actual: r.panorama.position, guess: r.guess, label: String(i + 1) })),
    [state.rounds],
  );
  // All rounds of a game share one map kind unless a fallback happened; Google pins need a Google map.
  const mapKind = state.rounds.some((r) => getProvider(r.panorama.provider).mapKind === 'google') ? 'google' : 'leaflet';
  const providers = [...new Set(state.rounds.map((r) => getProvider(r.panorama.provider).name))].join(', ');

  const shareText = [
    `GeoGessr · ${map.name} : ${formatScore(total)} / ${formatScore(MAX_GAME_SCORE)} points`,
    ...state.rounds.map(
      (r, i) => `Manche ${i + 1} : ${formatScore(r.score)} pts (${r.distanceKm === null ? 'sans réponse' : formatDistance(r.distanceKm)})`,
    ),
  ].join('\n');

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied('ok');
    } catch {
      setCopied('error');
    }
    window.setTimeout(() => setCopied(null), 2500);
  };

  return (
    <main className="summary-screen">
      <MapView kind={mapKind} className="summary-screen__map" initialView={map.view} pairs={pairs} ariaLabel="Carte récapitulative des 5 manches" />
      <section className="summary-card" aria-labelledby="summary-title">
        <span className="eyebrow">
          Partie terminée · {map.name} · {providers}
        </span>
        <h1 id="summary-title" className="summary-card__score">
          {formatScore(animatedTotal)}
          <small> / {formatScore(MAX_GAME_SCORE)}</small>
        </h1>
        <p className="summary-card__rank">{gameRank(total)}</p>
        {state.outcome?.isBestOverall ? (
          <p className="badge badge--gold">
            <IconTrophy size={16} /> Nouveau record personnel !
          </p>
        ) : state.outcome?.isBestOnMap ? (
          <p className="badge badge--gold">
            <IconTrophy size={16} /> Meilleur score sur la carte « {map.name} » !
          </p>
        ) : null}

        <ol className="round-list">
          {state.rounds.map((r, i) => (
            <li key={`${r.panorama.provider}-${r.panorama.id}`} className="round-list__item">
              <span className="round-list__num">{i + 1}</span>
              <span className="round-list__distance">{r.distanceKm === null ? 'Sans réponse' : formatDistance(r.distanceKm)}</span>
              <span className="round-list__bar" aria-hidden="true">
                <span style={{ width: `${(r.score / MAX_ROUND_SCORE) * 100}%` }} />
              </span>
              <span className="round-list__score">{formatScore(r.score)}</span>
            </li>
          ))}
        </ol>

        <p className="summary-card__settings">
          Temps par manche : {formatTimeLimit(state.settings.timeLimit)} · Déplacements {state.settings.allowMove ? 'autorisés' : 'interdits'}
        </p>

        <div className="summary-card__actions">
          <button type="button" className="btn btn--primary btn--lg" onClick={onReplay} autoFocus>
            <IconRefresh size={18} /> Rejouer
          </button>
          <button type="button" className="btn btn--ghost" onClick={copy}>
            <IconCopy size={18} /> {copied === 'ok' ? 'Copié !' : copied === 'error' ? 'Copie impossible' : 'Copier le résultat'}
          </button>
          <button type="button" className="btn btn--ghost" onClick={onHome}>
            <IconHome size={18} /> Menu principal
          </button>
        </div>
      </section>
    </main>
  );
}
